import { Award, Calendar, Mail, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import BackButton from "../components/BackButton";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import EventService from "../services/APIs/EventService";

interface LoginUserSponsor {
  membership_number: string | null;
  event_id: string;
  sponsor_id: string;
  name: string;
  email: string;
  phone: string;
  sponsor_amount: string;
  category: string;
  sponsor_title: string;
  sponsor_date: string;
  status: string;
  organization_id: string;
}

const Sponsorship = () => {
  const [sponsors, setSponsors] = useState<LoginUserSponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await EventService.getLoginUserSponsorList(false);
        if (response?.data && Array.isArray(response.data)) {
          setSponsors(response.data);
        } else {
          setSponsors([]);
        }
      } catch (error) {
        console.error("Failed to fetch sponsor list:", error);
        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div>
              <h1 className="text-lg font-bold text-primary md:text-xl">
                Sponsorship
              </h1>
              <p className="text-xs text-muted-foreground">Partner with us</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-8 text-center">
          <h2 className="mb-3 text-2xl font-bold md:text-3xl">
            Become a Sponsor
          </h2>
          <p className="text-muted-foreground">
            Partner with City Collab to create lasting impact in our community.
            Choose the sponsorship package that aligns with your
            organization&apos;s goals.
          </p>
        </div>

        {loading && (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Loading your sponsorships...
          </div>
        )}

        {!loading && sponsors.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No sponsorship records found for your account.
          </div>
        )}

        {!loading && sponsors.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sponsors.map(item => (
              <Card
                key={`${item.sponsor_id}-${item.event_id}`}
                className="transition-all"
              >
                <CardHeader>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{item.category}</Badge>
                    <Badge
                      variant={
                        item.status?.toLowerCase() === "paid"
                          ? "default"
                          : "outline"
                      }
                    >
                      {item.status}
                    </Badge>
                  </div>
                  <div className="mb-2 flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    <CardTitle className="text-xl">{item.name}</CardTitle>
                  </div>
                  <CardDescription className="text-base font-medium text-foreground">
                    {item.sponsor_title}
                  </CardDescription>
                  <div className="pt-2">
                    <span className="text-3xl font-bold text-primary">
                      ${item.sponsor_amount}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="break-all">{item.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4 shrink-0 text-primary" />
                    <span>{item.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 shrink-0 text-primary" />
                    <span>{item.sponsor_date}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Sponsorship;
